import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Info, X } from 'lucide-react';
import { useTheme } from '../../contexts/ThemeContext';
import { Button } from '../ui/Button';
interface ForwardingCompatibilitySheetProps {
  open: boolean;
  onClose: () => void;
}
const FORWARDING_OPTIONS = ["When you're busy", "When you don't answer", 'When your phone is off or unreachable', 'All incoming calls'];

/** Full details behind the "Confirm call forwarding" notice on Choose your plan. */
export function ForwardingCompatibilitySheet({
  open,
  onClose
}: ForwardingCompatibilitySheetProps) {
  const {
    isLight
  } = useTheme();
  const muted = isLight ? 'text-slate-500' : 'text-zinc-400';
  return <AnimatePresence data-id="element-345" data-mp="32ugptl0ffk4e">
      {open && <>
          <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} transition={{
        duration: 0.2
      }} onClick={onClose} className="absolute inset-0 z-40 bg-black/50 backdrop-blur-sm" data-id="element-346" data-mp="153niu1quwjvj" />
          <motion.div role="dialog" aria-modal="true" aria-labelledby="compat-title" initial={{
        y: '100%'
      }} animate={{
        y: 0
      }} exit={{
        y: '100%'
      }} transition={{
        duration: 0.28,
        ease: [0.23, 1, 0.32, 1]
      }} className={`absolute inset-x-0 bottom-0 z-50 max-h-[90%] overflow-y-auto rounded-t-3xl px-5 pb-6 pt-3 ${isLight ? 'bg-white' : 'bg-[#141414]'}`} data-id="element-347" data-mp="318mggsxg6v5m">
            <div className={`mx-auto mb-4 h-1 w-10 rounded-full ${isLight ? 'bg-slate-200' : 'bg-white/15'}`} aria-hidden data-id="element-348" data-mp="3ouj72fzw7c1r" />

            <div className="flex items-start justify-between gap-3" data-id="element-349" data-mp="3b2d1toya2ph3">
              <h2 id="compat-title" className={`text-lg font-bold tracking-tight ${isLight ? 'text-slate-900' : 'text-white'}`} data-id="element-350" data-mp="3f4q5m7tgsdb8">
                Before you get started
              </h2>
              <button type="button" onClick={onClose} aria-label="Close" className={`-mr-1.5 -mt-0.5 rounded-full p-1.5 transition-colors ${isLight ? 'text-slate-400 hover:bg-slate-100' : 'text-zinc-500 hover:bg-white/10'}`} data-id="element-351" data-mp="bhzls9z7irel">
                <X className="h-4 w-4" data-id="element-352" data-mp="cb71qr0rvruy" />
              </button>
            </div>
            <p className={`mt-1.5 text-[13px] leading-relaxed ${muted}`} data-id="element-353" data-mp="7dor8w73x4ku">
              Vexu uses call forwarding to answer calls from your existing number. Before subscribing, please check with
              your mobile provider that your plan supports call forwarding and confirm how to activate it.
            </p>

            <p className={`mb-2 mt-4 text-xs font-semibold ${isLight ? 'text-slate-700' : 'text-zinc-300'}`} data-id="element-354" data-mp="3ps7lqsq7rgcv">
              Forwarding options
            </p>
            <ul className={`rounded-2xl border ${isLight ? 'divide-y divide-slate-100 border-slate-200' : 'divide-y divide-white/[0.06] border-white/10'}`} data-id="element-355" data-mp="3fsyva9uk4jv9">
              {FORWARDING_OPTIONS.map(label => <li key={label} className={`flex items-center gap-2.5 px-3.5 py-2.5 text-[13px] ${isLight ? 'text-slate-800' : 'text-zinc-200'}`} data-id="element-356" data-mp="30oxampf8806t">
                  <span className="h-1.5 w-1.5 flex-shrink-0 rounded-full bg-vexu" aria-hidden data-id="element-357" data-mp="3ap66211e9hl5" />
                  {label}
                </li>)}
            </ul>

            <div className={`mt-3 flex gap-2.5 rounded-2xl p-3.5 ${isLight ? 'bg-slate-50' : 'bg-white/[0.04]'}`} data-id="element-358" data-mp="2hzz936hl9020">
              <Info className={`mt-0.5 h-4 w-4 flex-shrink-0 ${isLight ? 'text-vexu-700' : 'text-vexu'}`} data-id="element-359" data-mp="2mzlp1noswuei" />
              <p className={`text-xs leading-relaxed ${muted}`} data-id="element-360" data-mp="2rwvaytczenxl">
                Please confirm the correct activation codes or phone settings with your mobile provider. During setup, you
                can use Vexu's suggested activation codes or follow the instructions provided by your carrier. You only need
                to enable the forwarding options you want Vexu to handle.
              </p>
            </div>

            <Button fullWidth onClick={onClose} className="mt-5" data-id="element-361" data-mp="9s3vnyuhoqbq">
              Got it
            </Button>
          </motion.div>
        </>}
    </AnimatePresence>;
}